import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import filter from 'lodash/filter';
import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Grid, Header, Segment } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import NavHeader from './NavHeader';
import { getVistPermissions } from '../../actions';
import {
  canReadProdukte,
  canReadRezepte,
  canReadRohstoffe,
  hasBenutzerUiAccess,
  hasManagementUiAccess,
  selectIsAdmin,
  selectPermissions,
} from '../../helpers/permissions';
import config from '@plone/volto/registry';

import backSVG from '@plone/volto/icons/back.svg';

import rohstoffeGIF from './icons/navi_rohstoffe_normal.gif';
import rezepteGIF from './icons/navi_rezepte_normal.gif';
import produkteGIF from './icons/navi_produkte_normal.gif';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  versionoverview: {
    id: 'Version Overview',
    defaultMessage: 'Version Overview',
  },
  moderatecomments: {
    id: 'Moderate Comments',
    defaultMessage: 'Moderate Comments',
  },
  usersandgroups: {
    id: 'Users and Groups',
    defaultMessage: 'Users and Groups',
  },
  users: {
    id: 'Users',
    defaultMessage: 'Users',
  },
  groups: {
    id: 'Groups',
    defaultMessage: 'Groups',
  },
});

const Rezeptdatenbank = ({
  controlpanelsRequest,
  pathname,
  token,
  permissions,
  isAdmin,
  getVistPermissions,
  intl,
}) => {
  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    getVistPermissions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Error
    if (controlpanelsRequest.loading && controlpanelsRequest.error) {
      setError(controlpanelsRequest.error);
    }
    // eslint-disable-next-line
  }, [controlpanelsRequest.loading, controlpanelsRequest.error]);

  // Visibility per tile is a semantic check derived from the
  // @vist_permissions payload (see helpers/permissions.js).
  // Gruppen/Rezeptgruppen are admin-only pending TASK-90 Step 11
  // which will disable them from the dashboard entirely.
  const controlpanels = [
    {
      '@id': 'rezeptdatenbank/rohstoffe',
      group: 'Haupttabellen',
      title: 'Rohstoffe',
      image: rohstoffeGIF,
      visible: canReadRohstoffe(permissions),
    },
    {
      '@id': 'rezeptdatenbank/rezepte',
      group: 'Haupttabellen',
      title: 'Rezepte',
      image: rezepteGIF,
      visible: canReadRezepte(permissions),
    },
    {
      '@id': 'rezeptdatenbank/produkte',
      group: 'Haupttabellen',
      title: 'Produkte',
      image: produkteGIF,
      visible: canReadProdukte(permissions),
    },
    {
      '@id': 'rezeptdatenbank/bezuege',
      group: 'Haupttabellen',
      title: 'Bezüge',
      id: 'dexterity-types',
      visible: hasManagementUiAccess(permissions),
    },
    {
      '@id': 'rezeptdatenbank/benutzer',
      group: 'Benutzerverwaltung',
      title: 'Benutzer',
      id: 'users',
      visible: hasBenutzerUiAccess(permissions),
    },
    {
      '@id': 'rezeptdatenbank/benutzergruppen',
      group: 'Benutzerverwaltung',
      title: 'Benutzergruppen',
      visible: isAdmin,
    },
    {
      '@id': 'rezeptdatenbank/rezeptgruppen',
      group: 'Kategorisierung',
      title: 'Rezeptgruppen',
      id: 'language',
      visible: isAdmin,
    },
    {
      '@id': 'rezeptdatenbank/rezeptkategorien',
      group: 'Kategorisierung',
      title: 'Rezeptkategorien',
      visible: hasManagementUiAccess(permissions),
    },
    {
      '@id': 'rezeptdatenbank/zutatengruppen',
      group: 'Kategorisierung',
      title: 'Zutatengruppen',
      id: 'navigation',
      visible: hasManagementUiAccess(permissions),
    },
    {
      '@id': 'rezeptdatenbank/produktkategorien',
      group: 'Kategorisierung',
      title: 'Produktkategorien',
      id: 'navigation',
      visible: hasManagementUiAccess(permissions),
    },
    {
      '@id': 'rezeptdatenbank/rohstoffkategorien',
      group: 'Kategorisierung',
      title: 'Rohstoffkategorien',
      id: 'navigation',
      visible: hasManagementUiAccess(permissions),
    },
  ];
  const groups = map(uniqBy(controlpanels, 'group'), 'group');
  const { controlPanelsIcons: icons } = config.settings;

  const filteredControlpanels = filter(controlpanels, 'visible');
  // Drop groups whose tiles are all hidden.
  const filteredGroups = filter(groups, (group) =>
    filter(filteredControlpanels, { group }).length > 0,
  );

  if (error) {
    return <Error error={error} />;
  }

  if (!token) {
    setError({ status: 401 });
  }

  return (
    <div className="view-wrapper">
      <Helmet title="Rezeptdatenbank" />
      <NavHeader />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>Rezeptdatenbank</h1>
              </div>
            </div>
          </Segment>
          {map(filteredGroups, (group) => [
            <Segment key={`header-${group}`} secondary>
              {group}
            </Segment>,
            <Segment key={`body-${group}`} attached>
              <Grid columns={6}>
                <Grid.Row>
                  {map(
                    filter(filteredControlpanels, { group }),
                    (controlpanel) => (
                      <Grid.Column key={controlpanel.id}>
                        <Link to={`/controlpanel/${controlpanel['@id']}`}>
                          <Header as="h3" icon textAlign="center">
                            {controlpanel?.image ? (
                              <img
                                className="rezeptdatenbank-icon"
                                src={controlpanel?.image}
                                size="48px"
                              />
                            ) : (
                              <Icon
                                name={icons?.[controlpanel.id] || icons.default}
                                size="48px"
                              />
                            )}
                            <Header.Content>
                              {controlpanel.title}
                            </Header.Content>
                          </Header>
                        </Link>
                      </Grid.Column>
                    ),
                  )}
                </Grid.Row>
              </Grid>
            </Segment>,
          ])}
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

Rezeptdatenbank.propTypes = {
  controlpanels: PropTypes.arrayOf(
    PropTypes.shape({
      '@id': PropTypes.string,
      group: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
  pathname: PropTypes.string.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  isAdmin: PropTypes.bool,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state, props) => ({
      controlpanels: state.controlpanels.controlpanels,
      controlpanelsRequest: state.controlpanels.list,
      pathname: props.location.pathname,
      token: state.userSession.token,
      permissions: selectPermissions(state),
      isAdmin: selectIsAdmin(state),
    }),
    { getVistPermissions },
  ),
)(Rezeptdatenbank);
